import React, { Component } from "react";
import { Button } from "primereact/button";
import FormField from "../../components/ui/FormField";
import { Pencil, Plus, Trash2, Check, Shield } from "lucide-react";
import DataTableToolbar from "../../components/ui/DataTableToolbar";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Chips } from "primereact/chips";
import { AppCard, AppBadge, PageLayout } from "../../components/ui";
import ExtendedDataTable from "../../components/ExtendedDataTable";
import { stringFilterMatchModeOptions } from "../Paginator";
import { SearchRequestBaseModel, SortOrderType } from "../../models/SearchRequestModel";
import { utils } from "../../utils/utils";
import { toastService } from "../../services/toastService";
import { apiScopeApiService } from "../../services/api/clients/ApiScopeApiService";
import { ApiScopeViewModel, CreateApiScopeRequest, UpdateApiScopeRequest } from "../../models/ApiScopeModel";

interface ApiScopesState {
    scopes: ApiScopeViewModel[];
    totalRecords: number;
    isLoading: boolean;
    showCreateDialog: boolean;
    showEditDialog: boolean;
    showDeleteDialog: boolean;
    selectedScope: ApiScopeViewModel | null;
    createForm: CreateApiScopeRequest;
    editForm: UpdateApiScopeRequest;
}

const defaultCreateForm = (): CreateApiScopeRequest => ({
    name: "",
    displayName: "",
    description: "",
    scopes: [],
});

const defaultEditForm = (): UpdateApiScopeRequest => ({
    displayName: "",
    description: "",
    scopes: [],
});

export default class ApiScopes extends Component<Record<string, never>, ApiScopesState> {
    private dataTable: ExtendedDataTable | null = null;

    constructor(props: Record<string, never>) {
        super(props);
        this.state = {
            scopes: [],
            totalRecords: 0,
            isLoading: false,
            showCreateDialog: false,
            showEditDialog: false,
            showDeleteDialog: false,
            selectedScope: null,
            createForm: defaultCreateForm(),
            editForm: defaultEditForm(),
        };
    }

    loadScopesData = async (searchRequest: SearchRequestBaseModel) => {
        this.setState({ isLoading: true });
        searchRequest.filters = utils.manageDataTableFilters(searchRequest.filters);
        const result = await apiScopeApiService.Search(searchRequest);
        if (!result.success) {
            toastService.error(result.message);
        } else {
            this.setState({ scopes: result.data, totalRecords: result.totalCount });
        }
        this.setState({ isLoading: false });
    };

    // ── Create ──────────────────────────────────────────────────────────────

    openCreateDialog = () => {
        this.setState({ showCreateDialog: true, createForm: defaultCreateForm() });
    };

    onCreateSubmit = async () => {
        const { createForm } = this.state;
        if (!createForm.name.trim()) {
            toastService.warn("Resource name is required", "Validation");
            return;
        }
        const result = await apiScopeApiService.Create(createForm);
        if (!result.success) {
            toastService.error(result.message);
            return;
        }
        toastService.success("API scope created");
        this.setState({ showCreateDialog: false });
        this.dataTable?.reset();
    };

    // ── Edit ────────────────────────────────────────────────────────────────

    openEditDialog = (scope: ApiScopeViewModel) => {
        this.setState({
            selectedScope: scope,
            showEditDialog: true,
            editForm: {
                displayName: scope.displayName,
                description: scope.description,
                scopes: [...scope.scopes],
            },
        });
    };

    onEditSubmit = async () => {
        const { selectedScope, editForm } = this.state;
        if (!selectedScope) return;
        const result = await apiScopeApiService.Update(selectedScope.name, editForm);
        if (!result.success) {
            toastService.error(result.message);
            return;
        }
        toastService.success("API scope updated");
        this.setState({ showEditDialog: false, selectedScope: null });
        this.dataTable?.reset();
    };

    // ── Delete ──────────────────────────────────────────────────────────────

    openDeleteDialog = (scope: ApiScopeViewModel) => {
        this.setState({ selectedScope: scope, showDeleteDialog: true });
    };

    onDeleteConfirm = async () => {
        const { selectedScope } = this.state;
        if (!selectedScope) return;
        const result = await apiScopeApiService.Delete(selectedScope.name);
        if (!result.success) {
            toastService.error(result.message);
            return;
        }
        toastService.success("API scope deleted");
        this.setState({ showDeleteDialog: false, selectedScope: null });
        this.dataTable?.reset();
    };

    // ── Helpers ─────────────────────────────────────────────────────────────

    updateCreateForm = (field: keyof CreateApiScopeRequest, value: any) => {
        this.setState((prev) => ({ createForm: { ...prev.createForm, [field]: value } }));
    };

    updateEditForm = (field: keyof UpdateApiScopeRequest, value: any) => {
        this.setState((prev) => ({ editForm: { ...prev.editForm, [field]: value } }));
    };

    // ── Column renderers ─────────────────────────────────────────────────────

    scopesBody = (rowData: ApiScopeViewModel) => (
        <div className="flex gap-1 flex-wrap">
            {rowData.scopes.map((s, i) => (
                <AppBadge key={i} variant="info">{s}</AppBadge>
            ))}
        </div>
    );

    actionsBody = (rowData: ApiScopeViewModel) => (
        <div className="flex items-center gap-1">
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); this.openEditDialog(rowData); }}
                className="p-1.5 rounded-md text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/20 transition-colors"
                title="Edit"
            >
                <Pencil size={15} />
            </button>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); this.openDeleteDialog(rowData); }}
                className="p-1.5 rounded-md text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:text-danger-400 dark:hover:bg-danger-900/20 transition-colors"
                title="Delete"
            >
                <Trash2 size={15} />
            </button>
        </div>
    );

    renderTableHeader = (hasActiveFilters: boolean) => (
        <DataTableToolbar
            onClear={() => this.dataTable?.reset()}
            hasActiveFilters={hasActiveFilters}
        />
    );

    // ── Dialogs ──────────────────────────────────────────────────────────────

    renderCreateDialog = () => {
        const { createForm } = this.state;
        return (
            <Dialog
                header="Create API Scope"
                visible={this.state.showCreateDialog}
                style={{ width: "560px" }}
                onHide={() => this.setState({ showCreateDialog: false })}
                footer={
                    <>
                        <Button label="Cancel" outlined onClick={() => this.setState({ showCreateDialog: false })} />
                        <Button label="Create" icon={<Check size={16} />} onClick={this.onCreateSubmit} />
                    </>
                }
            >
                <FormField.Grid cols={1}>
                    <FormField label="Resource Name" required hint="e.g. Tenn.CRM">
                        <InputText className="w-full" value={createForm.name} onChange={(e) => this.updateCreateForm("name", e.target.value)} />
                    </FormField>
                    <FormField label="Display Name">
                        <InputText className="w-full" value={createForm.displayName} onChange={(e) => this.updateCreateForm("displayName", e.target.value)} />
                    </FormField>
                    <FormField label="Description">
                        <InputText className="w-full" value={createForm.description} onChange={(e) => this.updateCreateForm("description", e.target.value)} />
                    </FormField>
                    <FormField label="Scopes" hint="press Enter to add">
                        <Chips className="w-full" value={createForm.scopes} onChange={(e) => this.updateCreateForm("scopes", e.value ?? [])} placeholder="e.g. Tenn.CRM.read" />
                    </FormField>
                </FormField.Grid>
            </Dialog>
        );
    };

    renderEditDialog = () => {
        const { editForm, selectedScope } = this.state;
        return (
            <Dialog
                header={`Edit: ${selectedScope?.name ?? ""}`}
                visible={this.state.showEditDialog}
                style={{ width: "560px" }}
                onHide={() => this.setState({ showEditDialog: false })}
                footer={
                    <>
                        <Button label="Cancel" outlined onClick={() => this.setState({ showEditDialog: false })} />
                        <Button label="Save" icon={<Check size={16} />} onClick={this.onEditSubmit} />
                    </>
                }
            >
                <FormField.Grid cols={1}>
                    <FormField label="Display Name">
                        <InputText className="w-full" value={editForm.displayName} onChange={(e) => this.updateEditForm("displayName", e.target.value)} />
                    </FormField>
                    <FormField label="Description">
                        <InputText className="w-full" value={editForm.description} onChange={(e) => this.updateEditForm("description", e.target.value)} />
                    </FormField>
                    <FormField label="Scopes" hint="press Enter to add">
                        <Chips className="w-full" value={editForm.scopes} onChange={(e) => this.updateEditForm("scopes", e.value ?? [])} placeholder="e.g. Tenn.CRM.read" />
                    </FormField>
                </FormField.Grid>
            </Dialog>
        );
    };

    renderDeleteDialog = () => (
        <Dialog
            header="Confirm Delete"
            visible={this.state.showDeleteDialog}
            style={{ width: "400px" }}
            onHide={() => this.setState({ showDeleteDialog: false })}
            footer={
                <>
                    <Button label="Cancel" outlined onClick={() => this.setState({ showDeleteDialog: false })} />
                    <Button label="Delete" icon={<Trash2 size={16} />} severity="danger" onClick={this.onDeleteConfirm} />
                </>
            }
        >
            <p>Are you sure you want to delete <strong>{this.state.selectedScope?.name}</strong>?</p>
            <small className="text-neutral-400 dark:text-neutral-500">This will fail if any client is using scopes from this resource.</small>
        </Dialog>
    );

    render() {
        return (
            <PageLayout heading="API Scopes" icon={Shield} actions={<Button label="Add Scope" icon={<Plus size={16} />} onClick={this.openCreateDialog} />}>
                <AppCard>
                    <AppCard.Body className="!p-0">
                        <ExtendedDataTable
                            ref={(ref) => (this.dataTable = ref)}
                            header={this.renderTableHeader}
                            value={this.state.scopes}
                            onChange={this.loadScopesData}
                            loading={this.state.isLoading}
                            totalRecords={this.state.totalRecords}
                            defaultSortField="name"
                            defaultSortOrder={SortOrderType.Ascending}
                        >
                            <Column {...({ alwaysVisible: true } as any)} field="name" header="Resource Name" filterMatchModeOptions={stringFilterMatchModeOptions} />
                            <Column field="displayName" header="Display Name" filterMatchModeOptions={stringFilterMatchModeOptions} />
                            <Column field="description" header="Description" filterMatchModeOptions={stringFilterMatchModeOptions} />
                            <Column {...({ alwaysVisible: true } as any)} header="Scopes" body={this.scopesBody} />
                            <Column header="Actions" body={this.actionsBody} style={{ width: "120px" }} />
                        </ExtendedDataTable>
                    </AppCard.Body>
                </AppCard>
                {this.renderCreateDialog()}
                {this.renderEditDialog()}
                {this.renderDeleteDialog()}
            </PageLayout>
        );
    }
}
